import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { joiResolver } from '@hookform/resolvers/joi';

import { useAppContext } from '../../../../app/Context';
import { updatePollQuestion } from '../../../../db/polls';
import { usePollContext } from '../../Context';
import { FormValues } from './types';
import { finalValidationSchema, validationSchema } from './validators';

export interface UsePollQuestionForm {
  shouldUseRequiredValidator: boolean;
}

const usePollQuestionForm = ({ shouldUseRequiredValidator }: UsePollQuestionForm) => {
  const { isPresenting } = useAppContext();
  const { poll, setPoll } = usePollContext();

  const formMethods = useForm<FormValues>({
    resolver: (values, ...args) => {
      if (isPresenting || shouldUseRequiredValidator) {
        return joiResolver(finalValidationSchema)(values, ...args);
      }
      return joiResolver(validationSchema)(values, ...args);
    },
    defaultValues: {
      description: '',
      attachments: [],
      options: [],
    },
    mode: 'all',
  });

  const { setValue } = formMethods;

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    (data) => {
      if (!poll?.id) return;

      const questionId = poll.questions[0];
      if (!questionId) return;

      updatePollQuestion({
        classId: poll.classId,
        pollId: poll.id,
        questionId,
        description: data.description,
        attachments: data.attachments,
        options: data.options,
      });
    },
    [poll?.classId, poll?.id, poll?.questions[0]]
  );

  const onLocalSubmit: SubmitHandler<FormValues> = useCallback(
    (data) => {
      if (!poll?.id) return;

      const questionId = poll.questions[0];
      if (!questionId) return;

      setPoll({
        ...poll,
        questionsById: {
          ...poll.questionsById!,
          [questionId]: {
            ...poll.questionsById![questionId]!,
            description: data.description,
            attachments: data.attachments,
            options: data.options.map((option, index) => {
              const questionOption = poll.questionsById![questionId]!.options[index];
              return {
                ...questionOption,
                ...option,
                // set 0 because while adding a option there is no selections
                totalSelections: 0,
                num: index + 1,
              };
            }),
          },
        },
      });
    },
    [JSON.stringify(poll)]
  );

  useEffect(() => {
    if (!poll?.id) return;

    const questionId = poll.questions[0];
    const question = poll.questionsById?.[questionId];
    if (!question) return;

    setValue('description', question.description);
    setValue('attachments', question.attachments);
    setValue('options', question.options);
  }, [setValue, poll?.questions, poll?.questionsById]);

  return { formMethods, onSubmit, onLocalSubmit };
};

export default usePollQuestionForm;
